import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useAcceptMeetingSlot,
  useCancelScheduledMeeting,
  useLogMeetingJoin,
  useDeclineProposal,
} from "@/hooks/useScheduledMeetings";
import { useAcceptRescheduleSlot, useDeclineRescheduleProposal } from "@/hooks/useRescheduleMeeting";
import { useMeetingCardPrefs } from "@/hooks/useMeetingCardPrefs";
import { useAuth } from "@/lib/auth";
import { isScheduledStatus } from "@/lib/meetingStatus";
import { format, isPast, formatDistanceToNow } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import {
  Video,
  Calendar,
  Clock,
  Check,
  X,
  ExternalLink,
  Loader2,
  CalendarClock,
  MapPin,
  AlertTriangle,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Minimize2,
} from "lucide-react";

interface ProposalSlot {
  id: string;
  proposal_id: string;
  slot_start_at: string;
  slot_end_at: string;
  is_selected: boolean;
}

interface MeetingProposal {
  id: string;
  meeting_id: string;
  proposed_by: string;
  note: string | null;
  status: string;
  created_at: string;
  slots: ProposalSlot[];
}

interface ScheduledMeeting {
  id: string;
  thread_id: string;
  mentor_id: string | null;
  entrepreneur_id: string | null;
  status: string;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  timezone: string;
  duration_minutes: number;
  zoom_meeting_id: string | null;
  zoom_join_url: string | null;
  zoom_start_url: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  reschedule_pending?: boolean;
  pending_proposal_id?: string | null;
}

interface MentorshipRequest {
  mentor_id: string;
  entrepreneur_id: string;
  mentor?: { full_name: string } | null;
  entrepreneur?: { full_name: string } | null;
  topic?: { name: string } | null;
}

interface ScheduledMeetingCardProps {
  meeting: ScheduledMeeting | null;
  proposals: MeetingProposal[];
  mentorshipRequest: MentorshipRequest;
  isMentor: boolean;
  onReschedule?: () => void;
  threadId?: string;
}

export function ScheduledMeetingCard({
  meeting,
  proposals,
  mentorshipRequest,
  isMentor,
  onReschedule,
  threadId,
}: ScheduledMeetingCardProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const acceptSlot = useAcceptMeetingSlot();
  const acceptRescheduleSlot = useAcceptRescheduleSlot();
  const cancelMeeting = useCancelScheduledMeeting();
  const logJoin = useLogMeetingJoin();
  const declineProposal = useDeclineProposal();
  const declineReschedule = useDeclineRescheduleProposal();

  // Meeting card minimize/expand state
  const { isMinimized, toggleMinimized, resetMinimized, isToggling } = useMeetingCardPrefs(threadId || meeting?.thread_id);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  
  // Track which slot is being accepted (for button loading state)
  const [acceptingSlotId, setAcceptingSlotId] = useState<string | null>(null);
  
  // Decline proposal dialog state (for recipient)
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  
  // Cancel proposal dialog state (for proposer)
  const [cancelProposalDialogOpen, setCancelProposalDialogOpen] = useState(false);
  const [cancelProposalReason, setCancelProposalReason] = useState("");
  
  // Track Zoom link creation timeout (20s) for retry functionality
  const [zoomCreationStartTime, setZoomCreationStartTime] = useState<number | null>(null);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [isRetryingZoom, setIsRetryingZoom] = useState(false);
  
  // Instrumentation: Track slot visibility issues (dev/admin only)
  const [slotDiagnostics, setSlotDiagnostics] = useState<{
    dbSlotCount: number | null;
    propsSlotCount: number;
    mismatch: boolean;
    checked: boolean;
  }>({ dbSlotCount: null, propsSlotCount: 0, mismatch: false, checked: false });
  
  // Reset diagnostics when proposals change (e.g., after decline)
  const openProposalCount = proposals.filter(p => p.status === "open").length;
  useEffect(() => {
    // Reset checked state when open proposals change so we re-check
    setSlotDiagnostics(prev => ({ ...prev, checked: false }));
  }, [openProposalCount]);

  // PHASE 8: Determine meeting state - support new pipeline statuses
  // Shared with the fetch layers via src/lib/meetingStatus.ts so the list
  // queries and this card can never disagree about what "scheduled" means.
  const isScheduled = isScheduledStatus(meeting?.status) && meeting?.scheduled_start_at;
  const isCancelled = meeting?.status === "cancelled";
  const isFailed = meeting?.status === "failed";
  const hasOpenProposal = proposals.some((p) => p.status === "open");
  
  // Status configuration for UI display
  const statusConfig: Record<string, { label: string; colorClass: string; loading?: boolean }> = {
    'proposed': { label: 'Proposed', colorClass: 'text-blue-600 border-blue-300' },
    'scheduled': { label: 'Scheduled', colorClass: 'text-yellow-600 border-yellow-300' },
    'zoom_creating': { label: 'Creating Zoom...', colorClass: 'text-orange-600 border-orange-300', loading: true },
    'zoom_ready': { label: 'Zoom Ready', colorClass: 'text-green-600 border-green-300' },
    'invites_sending': { label: 'Sending Invites...', colorClass: 'text-purple-600 border-purple-300', loading: true },
    'invites_sent': { label: 'Ready', colorClass: 'text-green-600 border-green-300' },
    'failed': { label: 'Failed - Retry', colorClass: 'text-red-600 border-red-300' },
  };
  
  // Check if meeting is upcoming or past
  const meetingTime = meeting?.scheduled_start_at ? new Date(meeting.scheduled_start_at) : null;
  const isUpcoming = meetingTime && !isPast(meetingTime);
  const isPastMeeting = meetingTime && isPast(meetingTime);

  // Auto-expand when there are open proposals or reschedule is pending
  // Note: resetMinimized is now memoized with useCallback, so this won't cause loops
  useEffect(() => {
    if (hasOpenProposal || meeting?.reschedule_pending) {
      resetMinimized();
    }
    // resetMinimized is stable (memoized), safe to include
  }, [hasOpenProposal, meeting?.reschedule_pending, resetMinimized]);

  // Track Zoom link creation timeout - show retry after 20s
  useEffect(() => {
    const isWaitingForZoom = meeting?.status === "scheduled" && !meeting.zoom_join_url && isUpcoming;
    
    if (isWaitingForZoom) {
      // Start tracking if not already
      if (!zoomCreationStartTime) {
        setZoomCreationStartTime(Date.now());
        setShowRetryButton(false);
      }
      
      // Set up timeout to show retry button after 20 seconds
      const timeoutId = setTimeout(() => {
        setShowRetryButton(true);
      }, 20000);
      
      return () => clearTimeout(timeoutId);
    } else {
      // Reset when Zoom link appears or meeting is no longer waiting
      setZoomCreationStartTime(null);
      setShowRetryButton(false);
      setIsRetryingZoom(false);
    }
  }, [meeting?.status, meeting?.zoom_join_url, isUpcoming, zoomCreationStartTime]);

  // Handle retry Zoom creation
  const handleRetryZoomCreation = async () => {
    if (!meeting?.id || isRetryingZoom) return;
    
    setIsRetryingZoom(true);
    setShowRetryButton(false);
    
    try {
      console.log("[ScheduledMeetingCard] Retrying Zoom creation for meeting:", meeting.id);
      
      // Log retry attempt
      await supabase.from("scheduling_debug_events").insert({
        stage: "zoom_retry_attempt",
        thread_id: threadId || meeting.thread_id,
        meeting_id: meeting.id,
        payload: { 
          retry_at: new Date().toISOString(),
          original_start_time: zoomCreationStartTime,
          elapsed_ms: zoomCreationStartTime ? Date.now() - zoomCreationStartTime : null,
        },
      });
      
      // Find the selected slot for this meeting
      const { data: proposal } = await supabase
        .from("mentorship_meeting_proposals")
        .select("id")
        .eq("meeting_id", meeting.id)
        .eq("status", "accepted")
        .single();
      
      if (!proposal) {
        toast.error("Could not find meeting proposal. Please refresh the page.");
        setIsRetryingZoom(false);
        setShowRetryButton(true);
        return;
      }
      
      const { data: slot } = await supabase
        .from("mentorship_meeting_proposal_slots")
        .select("id")
        .eq("proposal_id", proposal.id)
        .eq("is_selected", true)
        .single();
      
      if (!slot) {
        toast.error("Could not find selected time slot. Please refresh the page.");
        setIsRetryingZoom(false);
        setShowRetryButton(true);
        return;
      }
      
      // Invoke Zoom creation edge function
      const { error } = await supabase.functions.invoke("create-zoom-meeting", {
        body: {
          duration: meeting.duration_minutes,
          slot_id: slot.id,
          meeting_id: meeting.id,
        },
      });
      
      if (error) {
        console.error("[ScheduledMeetingCard] Retry Zoom creation failed:", error);
        toast.error(`Failed to create Zoom: ${error.message || "Unknown error"}`);
        setShowRetryButton(true);
      } else {
        toast.success("Zoom link is being created...");
        // Reset start time to restart the 20s countdown
        setZoomCreationStartTime(Date.now());
        // Invalidate to pick up the new Zoom link
        queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", threadId || meeting.thread_id] });
      }
    } catch (err) {
      console.error("[ScheduledMeetingCard] Retry exception:", err);
      toast.error("Failed to retry Zoom creation. Please try again.");
      setShowRetryButton(true);
    } finally {
      setIsRetryingZoom(false);
    }
  };

  // Count total slots from props
  const propsSlotCount = proposals.reduce((acc, p) => acc + (p.slots?.length || 0), 0);

  // Instrumentation: Check DB slots vs props slots on mount
  useEffect(() => {
    const checkSlotMismatch = async () => {
      if (!meeting?.id || slotDiagnostics.checked) return;
      
      try {
        // Query all slots for proposals linked to this meeting
        const { data: proposalsData, error: proposalsError } = await supabase
          .from("mentorship_meeting_proposals")
          .select("id")
          .eq("meeting_id", meeting.id)
          .eq("status", "open");
        
        if (proposalsError) {
          console.warn("[ScheduledMeetingCard] Error fetching proposals:", proposalsError);
          return;
        }
        
        if (!proposalsData || proposalsData.length === 0) {
          setSlotDiagnostics({ dbSlotCount: 0, propsSlotCount, mismatch: propsSlotCount > 0, checked: true });
          return;
        }
        
        const proposalIds = proposalsData.map(p => p.id);
        
        const { data: slotsData, error: slotsError, count } = await supabase
          .from("mentorship_meeting_proposal_slots")
          .select("id", { count: "exact" })
          .in("proposal_id", proposalIds);
        
        if (slotsError) {
          console.warn("[ScheduledMeetingCard] Error fetching slots:", slotsError);
          return;
        }
        
        const dbSlotCount = count || slotsData?.length || 0;
        const mismatch = dbSlotCount !== propsSlotCount;
        
        setSlotDiagnostics({ dbSlotCount, propsSlotCount, mismatch, checked: true });
        
        if (mismatch) {
          console.error("[ScheduledMeetingCard] SLOT MISMATCH DETECTED - auto-retrying:", {
            dbSlotCount,
            propsSlotCount,
            meetingId: meeting.id,
            proposalIds,
          });
          
          // AUTO-RETRY: Clear cache entirely then invalidate to force fresh fetch
          queryClient.setQueryData(["open-proposals-with-slots", threadId], undefined);
          queryClient.setQueryData(["scheduled-meeting", threadId], undefined);
          
          // Force immediate refresh
          queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", threadId] });
          queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", threadId] });
          
          // Log to error_logs (non-blocking)
          void supabase.from("error_logs").insert({
            error_message: "Slot visibility mismatch detected - auto-retry triggered",
            error_details: `DB has ${dbSlotCount} slots but UI received ${propsSlotCount}`,
            component: "ScheduledMeetingCard",
            action: "slot_visibility_check_autoretry",
            metadata: {
              meeting_id: meeting.id,
              thread_id: threadId,
              db_slot_count: dbSlotCount,
              props_slot_count: propsSlotCount,
              proposal_ids: proposalIds,
            },
          });
        }
      } catch (err) {
        console.warn("[ScheduledMeetingCard] Slot check error:", err);
      }
    };
    
    checkSlotMismatch();
  }, [meeting?.id, propsSlotCount, slotDiagnostics.checked]);

  const handleAcceptSlot = useCallback(async (slot: ProposalSlot, proposal: MeetingProposal) => {
    // CRITICAL: Version verification - must be first line
    console.log("[ScheduledMeetingCard] handleAcceptSlot v2026.01.12.3 - SESSION VALIDATION", { 
      slotId: slot.id, 
      meetingId: meeting?.id,
      timestamp: new Date().toISOString() 
    });
    
    if (!meeting) return;

    // Session validation before proceeding - prevent "Load failed" errors
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error("[ScheduledMeetingCard] Session expired:", sessionError);
        toast.error("Your session has expired. Please refresh the page and try again.");
        return;
      }
    } catch (sessionCheckError) {
      console.error("[ScheduledMeetingCard] Session check failed:", sessionCheckError);
      toast.error("Connection error. Please refresh the page and try again.");
      return;
    }

    setAcceptingSlotId(slot.id);

    try {
      // The mutation now accepts instantly, then creates Zoom in background
      await acceptSlot.mutateAsync({
        slotId: slot.id,
        threadId: meeting.thread_id,
        duration: meeting.duration_minutes,
        meetingId: meeting.id,
      });
    } catch (error) {
      console.error("[ScheduledMeetingCard] Accept slot failed:", error);
      // Error toast is shown by the hook's onError
    } finally {
      setAcceptingSlotId(null);
    }
  }, [meeting, acceptSlot]);

  const handleCancel = async () => {
    if (!meeting) return;

    await cancelMeeting.mutateAsync({
      meetingId: meeting.id,
      threadId: meeting.thread_id,
      reason: cancelReason.trim() || undefined,
      zoomMeetingId: meeting.zoom_meeting_id || null,
      scheduledStartAt: meeting.scheduled_start_at,
    });

    setCancelDialogOpen(false);
    setCancelReason("");
  };

  const handleJoin = async (url: string) => {
    if (meeting) {
      // Log the join event (non-blocking)
      logJoin.mutate({ meetingId: meeting.id, source: "chat_card" });
    }
    // Open Zoom in new tab
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Handle declining a proposal (for recipient)
  const handleDeclineProposal = useCallback(async (proposal: MeetingProposal) => {
    if (!meeting) return;

    // Get the proposer's name for the system message
    const proposerName = proposal.proposed_by === mentorshipRequest.mentor_id
      ? mentorshipRequest.mentor?.full_name || "the mentor"
      : mentorshipRequest.entrepreneur?.full_name || "the entrepreneur";

    try {
      await declineProposal.mutateAsync({
        proposalId: proposal.id,
        threadId: meeting.thread_id,
        meetingId: meeting.id,
        reason: declineReason.trim() || undefined,
        proposedByName: proposerName,
      });
    } catch (error) {
      console.error("[ScheduledMeetingCard] Decline proposal failed:", error);
      // Error toast is shown by hook's onError
    } finally {
      setDeclineDialogOpen(false);
      setDeclineReason("");
    }
  }, [meeting, mentorshipRequest, declineProposal, declineReason]);

  // Handle cancelling a proposal (for proposer)
  const handleCancelProposal = useCallback(async (proposal: MeetingProposal) => {
    if (!meeting || !user) return;

    // Get the current user's name for the system message
    const myName = user.id === mentorshipRequest.mentor_id
      ? mentorshipRequest.mentor?.full_name || "the mentor"
      : mentorshipRequest.entrepreneur?.full_name || "the entrepreneur";

    try {
      await declineProposal.mutateAsync({
        proposalId: proposal.id,
        threadId: meeting.thread_id,
        meetingId: meeting.id,
        reason: cancelProposalReason.trim() || undefined,
        proposedByName: myName,
        isCancellation: true, // Flag to indicate this is a self-cancellation
      });
    } catch (error) {
      console.error("[ScheduledMeetingCard] Cancel proposal failed:", error);
      // Error toast is shown by hook's onError
    } finally {
      setCancelProposalDialogOpen(false);
      setCancelProposalReason("");
    }
  }, [meeting, user, mentorshipRequest, declineProposal, cancelProposalReason]);

  // If cancelled, show cancelled state
  if (isCancelled) {
    return (
      <Card className="border-l-4 border-l-muted bg-muted/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="font-medium">Meeting Cancelled</span>
          </div>
          {meeting?.cancellation_reason && (
            <p className="text-sm text-muted-foreground mt-2 italic">
              "{meeting.cancellation_reason}"
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // PHASE 8: If failed, show failed state with retry option
  if (isFailed && meeting) {
    return (
      <Card className="border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-950/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Meeting Setup Failed</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRetryZoomCreation}
              disabled={isRetryingZoom}
              className="text-red-600 border-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
            >
              {isRetryingZoom ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Retry
                </>
              )}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            There was a problem creating your Zoom meeting. Click Retry to try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  // PRIORITY FIX: Check for open proposals FIRST before showing scheduled meeting
  // This ensures recipients see proposal cards even when meeting is already scheduled (reschedule case)
  if (hasOpenProposal) {
    const otherUserProposal = proposals.find((p) => p.status === "open" && p.proposed_by !== user?.id);
    const myProposal = proposals.find((p) => p.status === "open" && p.proposed_by === user?.id);
    
    // Display the other user's proposal if available, otherwise show my own
    const displayProposal = otherUserProposal || myProposal;
    if (displayProposal) {
      const isProposer = displayProposal.proposed_by === user?.id;
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isReschedule = meeting?.reschedule_pending || (isScheduled && meeting.scheduled_start_at);

      return (
        <>
        <Card className={`border-l-4 ${isReschedule ? "border-l-blue-500" : "border-l-amber-500"}`}>
          <CardContent className="p-4">
            {/* Reschedule indicator */}
            {isReschedule && (
              <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded text-sm text-blue-700 dark:text-blue-300">
                <RefreshCw className="h-4 w-4" />
                <span>Reschedule proposal - the existing meeting will be updated if accepted</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <CalendarClock className="h-4 w-4 text-primary" />
              <span className="font-medium">
                {isProposer ? "You proposed" : "Meeting times proposed"}
              </span>
              <Badge variant="outline" className={isReschedule ? "text-blue-600 border-blue-300" : "text-amber-600 border-amber-300"}>
                <Clock className="h-3 w-3 mr-1" />
                {isReschedule ? "Reschedule Pending" : "Pending"}
              </Badge>
            </div>

            {displayProposal.note && (
              <p className="text-sm text-muted-foreground italic mb-3">
                "{displayProposal.note}"
              </p>
            )}

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                {isProposer ? "Proposed times (awaiting response):" : "Select a time:"}
              </Label>
              <div className="space-y-2">
                {displayProposal.slots
                  .sort((a, b) => new Date(a.slot_start_at).getTime() - new Date(b.slot_start_at).getTime())
                  .map((slot) => {
                    const slotDate = new Date(slot.slot_start_at);
                    const isSlotPast = isPast(slotDate);

                    return (
                      <div
                        key={slot.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isSlotPast ? "bg-muted/50 opacity-60" : "bg-background"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">
                              {format(slotDate, "EEEE, MMMM d")}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {format(slotDate, "h:mm a")}
                              {meeting?.timezone !== userTimezone && (
                                <span className="ml-2 text-xs">
                                  (Your time: {formatInTimeZone(slotDate, userTimezone, "h:mm a")})
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        {!isProposer && !isSlotPast && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                setSelectedSlotId(slot.id);
                                try {
                                  // Use reschedule accept if this is a reschedule proposal
                                  if (isReschedule && meeting) {
                                    await acceptRescheduleSlot.mutateAsync({
                                      slotId: slot.id,
                                      proposalId: displayProposal.id,
                                      meetingId: meeting.id,
                                      threadId: meeting.thread_id,
                                      oldZoomMeetingId: meeting.zoom_meeting_id || undefined,
                                      duration: meeting.duration_minutes,
                                    });
                                  } else {
                                    await handleAcceptSlot(slot, displayProposal);
                                  }
                                } catch (error) {
                                  console.error("[ScheduledMeetingCard] Accept slot failed:", error);
                                  // Error toast is shown by the hook's onError
                                } finally {
                                  setSelectedSlotId(null);
                                }
                              }}
                              disabled={acceptSlot.isPending || acceptRescheduleSlot.isPending || !!acceptingSlotId}
                            >
                              {(acceptSlot.isPending || acceptRescheduleSlot.isPending) && (selectedSlotId === slot.id || acceptingSlotId === slot.id) ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  <span className="text-xs">Accepting...</span>
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4 mr-1" />
                                  Accept
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setDeclineDialogOpen(true)}
                              disabled={declineProposal.isPending || declineReschedule.isPending}
                            >
                              {(declineProposal.isPending || declineReschedule.isPending) ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        )}
                        {isSlotPast && (
                          <Badge variant="secondary" className="text-xs">
                            Past
                          </Badge>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            {!isProposer && (
              <p className="text-xs text-muted-foreground mt-3">
                Can't make any of these times? You can decline or propose alternatives.
              </p>
            )}

            {isProposer && (
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Waiting for the other participant to select a time.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setCancelProposalDialogOpen(true)}
                  disabled={declineProposal.isPending}
                >
                  {declineProposal.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <X className="h-4 w-4 mr-1" />
                  )}
                  Cancel Proposal
                </Button>
              </div>
            )}

            {/* Show indicator if both users have proposed times */}
            {otherUserProposal && myProposal && !isProposer && (
              <div className="mt-3 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
                You've also proposed alternative times. Waiting for their response.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Decline Proposal Confirmation Dialog */}
        <AlertDialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Decline proposed meeting times?</AlertDialogTitle>
              <AlertDialogDescription>
                {isReschedule 
                  ? "This will decline the reschedule proposal. The original meeting will remain unchanged."
                  : "This will decline the current proposed times. The other person can propose new times."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="decline-reason">Reason (optional)</Label>
              <Textarea
                id="decline-reason"
                placeholder="e.g., 'These times don't work for me. I'll propose alternatives.'"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="mt-2"
                rows={2}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    if (isReschedule && meeting) {
                      await declineReschedule.mutateAsync({
                        proposalId: displayProposal.id,
                        meetingId: meeting.id,
                        threadId: meeting.thread_id,
                        reason: declineReason.trim() || undefined,
                      });
                    } else {
                      await handleDeclineProposal(displayProposal);
                      return; // handleDeclineProposal has its own finally block
                    }
                  } catch (error) {
                    console.error("[ScheduledMeetingCard] Decline reschedule failed:", error);
                    // Error toast is shown by hook's onError
                  } finally {
                    setDeclineDialogOpen(false);
                    setDeclineReason("");
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={declineProposal.isPending || declineReschedule.isPending}
              >
                {(declineProposal.isPending || declineReschedule.isPending) ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Decline
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cancel Proposal Confirmation Dialog (for proposer) */}
        <AlertDialog open={cancelProposalDialogOpen} onOpenChange={setCancelProposalDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel your proposed meeting times?</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel your proposed times. You can propose new times afterward.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="cancel-proposal-reason">Reason (optional)</Label>
              <Textarea
                id="cancel-proposal-reason"
                placeholder="e.g., 'My schedule changed. I'll propose new times.'"
                value={cancelProposalReason}
                onChange={(e) => setCancelProposalReason(e.target.value)}
                className="mt-2"
                rows={2}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Proposal</AlertDialogCancel>
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  await handleCancelProposal(displayProposal);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={declineProposal.isPending}
              >
                {declineProposal.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Cancel Proposal
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
      );
    }
  }

  // If scheduled (no open proposals), show the scheduled meeting card
  if (isScheduled && meeting.scheduled_start_at) {
    const meetingDate = new Date(meeting.scheduled_start_at);
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Minimized compact bar view
    if (isMinimized && isUpcoming) {
      return (
        <div className="flex items-center justify-between gap-3 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Video className="h-4 w-4 text-green-600 shrink-0" />
            <span className="text-sm font-medium truncate">
              Meeting: {format(meetingDate, "MMM d, h:mm a")}
            </span>
            <Badge variant="outline" className="text-green-600 border-green-300 shrink-0">
              Confirmed
            </Badge>
            {/* Show "Creating Zoom link..." badge when Zoom is pending */}
            {!meeting.zoom_join_url && !showRetryButton && (
              <Badge variant="outline" className="text-blue-600 border-blue-300 shrink-0 animate-pulse">
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                Creating Zoom...
              </Badge>
            )}
            {/* Show timeout/retry badge when Zoom creation is taking too long */}
            {!meeting.zoom_join_url && showRetryButton && (
              <Badge 
                variant="outline" 
                className="text-amber-600 border-amber-300 shrink-0 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30"
                onClick={handleRetryZoomCreation}
              >
                {isRetryingZoom ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-3 w-3 mr-1" />
                )}
                {isRetryingZoom ? "Retrying..." : "Retry Zoom"}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {meeting.zoom_join_url && (
              <Button size="sm" variant="default" onClick={() => handleJoin(meeting.zoom_join_url!)}>
                <ExternalLink className="h-3 w-3 mr-1" />
                Join
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => toggleMinimized(false)}
              disabled={isToggling}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <>
        <Card className={`border-l-4 ${isUpcoming ? "border-l-green-500" : "border-l-muted"} relative`}>
          {/* Top right buttons: Minimize and Cancel */}
          <div className="absolute top-2 right-2 flex items-center gap-1">
            {isUpcoming && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={() => toggleMinimized(true)}
                  disabled={isToggling}
                  title="Minimize"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  onClick={() => setCancelDialogOpen(true)}
                  title="Cancel Meeting"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          <CardContent className="p-4 pr-16">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Video className="h-4 w-4 text-primary" />
                  <span className="font-medium">
                    {isUpcoming ? "Scheduled Meeting" : "Past Meeting"}
                  </span>
                  {isUpcoming && meeting.status && statusConfig[meeting.status] && (
                    <Badge variant="outline" className={`${statusConfig[meeting.status].colorClass} flex items-center gap-1`}>
                      {statusConfig[meeting.status].loading && (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      )}
                      {!statusConfig[meeting.status].loading && <Calendar className="h-3 w-3" />}
                      {statusConfig[meeting.status].label}
                    </Badge>
                  )}
                  {isUpcoming && meeting.status && !statusConfig[meeting.status] && (
                    <Badge variant="outline" className="text-green-600 border-green-300">
                      <Calendar className="h-3 w-3 mr-1" />
                      Confirmed
                    </Badge>
                  )}
                  {isPastMeeting && (
                    <Badge variant="secondary">
                      Completed
                    </Badge>
                  )}
                </div>

                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {format(meetingDate, "EEEE, MMMM d, yyyy")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{format(meetingDate, "h:mm a")} ({meeting.duration_minutes} min)</span>
                  </div>
                  {meeting.timezone !== userTimezone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span className="text-xs">
                        Your time: {formatInTimeZone(meetingDate, userTimezone, "h:mm a zzz")}
                      </span>
                    </div>
                  )}
                  {isUpcoming && (
                    <p className="text-muted-foreground">
                      {formatDistanceToNow(meetingDate, { addSuffix: true })}
                    </p>
                  )}
                </div>

                {/* Show "Creating Zoom link..." message when Zoom is pending */}
                {!meeting.zoom_join_url && isUpcoming && (
                  <div className="mt-4">
                    {showRetryButton ? (
                      // Timeout state: show retry option
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-amber-600 border border-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-md px-3 py-2">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Still working on your Zoom link...</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleRetryZoomCreation}
                            disabled={isRetryingZoom}
                          >
                            {isRetryingZoom ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Retrying...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Retry
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            This sometimes happens. Click Retry or refresh the page.
                          </p>
                        </div>
                      </div>
                    ) : (
                      // Normal waiting state
                      <>
                        <div className="flex items-center gap-2 text-sm text-blue-600 border border-blue-300 bg-blue-50 dark:bg-blue-950/30 rounded-md px-3 py-2 animate-pulse">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Creating your Zoom link...</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Your Zoom link will appear shortly. Calendar invite on its way!
                        </p>
                      </>
                    )}
                  </div>
                )}

                {meeting.zoom_join_url && isUpcoming && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => handleJoin(meeting.zoom_join_url!)}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Join Meeting
                    </Button>
                    {isMentor && meeting.zoom_start_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleJoin(meeting.zoom_start_url!)}
                      >
                        <Video className="h-4 w-4 mr-2" />
                        Start as Host
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {isUpcoming && (
                <div className="flex flex-col gap-2 shrink-0">
                  {onReschedule && (
                    <Button size="sm" variant="outline" onClick={onReschedule}>
                      Reschedule
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Meeting?</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel the scheduled meeting and notify the other participant.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="cancel-reason">Reason (optional)</Label>
              <Textarea
                id="cancel-reason"
                placeholder="e.g., 'Something came up, let's reschedule'"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-2"
                rows={2}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Meeting</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancel}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {cancelMeeting.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Cancel Meeting
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // Debug logging only (not shown to users) - slot mismatch for open proposals
  if (slotDiagnostics.checked && slotDiagnostics.mismatch && slotDiagnostics.dbSlotCount && slotDiagnostics.dbSlotCount > 0) {
    console.warn("[ScheduledMeetingCard] DB has slots but card is empty:", {
      dbSlotCount: slotDiagnostics.dbSlotCount,
      propsSlotCount: slotDiagnostics.propsSlotCount,
      meetingId: meeting?.id,
      hasOpenProposals: hasOpenProposal,
    });
  }

  // No open proposals = nothing to display (clean exit, no error card)
  return null;
}
